import { useState, useEffect, useRef, useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getWeek } from "date-fns";
import confetti from "canvas-confetti";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { MoreHorizontal, Flame, Pencil, Trash2, Target, FileText, Trophy, Archive, Medal, Layers, Link, ListOrdered, CheckCircle2, Clock, MapPin, BarChart2, Share2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { apiRequest } from "@/lib/queryClient";
import { formatDateInput, parseLocalDate } from "@/lib/dateUtils";
import { utcEntryDay } from "@/lib/trackerx-dates";
import EditHabitModal from "@/components/edit-habit-modal";
import CreateNoteModal from "@/components/create-note-modal";
import ShareDialog from "@/components/share-dialog";
import type { HabitWithStreakState, HabitEntry, Goal, HabitSequenceStep } from "@shared/schema";

interface HabitCardProps {
  habit: HabitWithStreakState;
  viewMode?: "grid" | "list";
  celebrationEnabled?: boolean;
}

// ─── TrackerX quantified-progress chip ──────────────────────────────────────
// Shows the current-period numeric total vs goal (e.g. "8 / 10 reps today") for
// any TrackerX trackers linked to this habit, without touching check-off logic.
interface LinkedTrackerSummary {
  trackerId: string;
  name: string;
  valueType: "number" | "weight" | "currency" | "time";
  unitLabel: string | null;
  period: "daily" | "weekly" | "monthly";
  goalValue: number | null;
  periodTotal: number;
}

function formatTrackerValue(value: number, t: LinkedTrackerSummary): string {
  if (t.valueType === "currency") return `$${value.toLocaleString()}`;
  return `${value}${t.unitLabel ? ` ${t.unitLabel}` : ""}`;
}

const PERIOD_LABEL: Record<LinkedTrackerSummary["period"], string> = {
  daily: "today",
  weekly: "this week",
  monthly: "this month",
};

function HabitTrackerXChips({ habitId }: { habitId: string }) {
  // Totals are aggregated server-side over the tracker's current period window,
  // so high-volume trackers never undercount. All habit cards share this one
  // batched query, so the whole page costs a single request.
  const { data: byHabit } = useQuery<Record<string, LinkedTrackerSummary[]>>({
    queryKey: ["/api/trackerx/habit-summaries"],
    queryFn: () => apiRequest("GET", "/api/trackerx/habit-summaries").then((r) => r.json()),
  });
  const summaries = byHabit?.[habitId] ?? [];

  if (summaries.length === 0) return null;

  return (
    <div className="flex items-center gap-1 mt-1 flex-wrap" onClick={(e) => e.stopPropagation()}>
      {summaries.map((t) => (
        <Badge
          key={t.trackerId}
          variant="outline"
          className="text-[10px] gap-1 font-normal"
          title={`${t.name} — ${PERIOD_LABEL[t.period]}`}
          data-testid={`chip-trackerx-${t.trackerId}`}
        >
          <BarChart2 className="w-3 h-3 text-muted-foreground" />
          <span className="font-semibold tabular-nums">
            {formatTrackerValue(t.periodTotal, t)}
            {t.goalValue != null && ` / ${formatTrackerValue(t.goalValue, t)}`}
          </span>
          <span className="text-muted-foreground">{PERIOD_LABEL[t.period]}</span>
        </Badge>
      ))}
    </div>
  );
}

export default function HabitCard({ habit, viewMode = "grid", celebrationEnabled = true }: HabitCardProps) {
  const queryClient = useQueryClient();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [createNoteModalOpen, setCreateNoteModalOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [streakCelebration, setStreakCelebration] = useState<{ streak: number; remaining: number } | null>(null);
  const [targetReached, setTargetReached] = useState(false);
  const [habitStackPhaseComplete, setHabitStackPhaseComplete] = useState<number | null>(null);
  const [habitStackCustomDays, setHabitStackCustomDays] = useState<string>("");
  const [habitStackSelectedDays, setHabitStackSelectedDays] = useState<string>("");
  const celebrationJustDismissedRef = useRef(false);
  const pendingTargetReachedRef = useRef(false);
  const pendingHabitStackRef = useRef<number | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const hasScrolledRef = useRef(false);
  const toggleMutationPromiseRef = useRef<Promise<any> | null>(null);

  // Sequencing state
  const [sequencingDialogOpen, setSequencingDialogOpen] = useState(false);
  const [sequencingPeriod, setSequencingPeriod] = useState<{ date: Date; key: string; completed: boolean } | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [completedStepIds, setCompletedStepIds] = useState<Set<string>>(new Set());

  // Fetch habit entries for the current period
  const { data: habitEntries = [] } = useQuery<HabitEntry[]>({
    queryKey: [`/api/habits/${habit.id}/entries`, habit.frequency],
    queryFn: async () => {
      const response = await fetch(`/api/habits/${habit.id}/entries?period=${habit.frequency}`);
      if (!response.ok) throw new Error('Failed to fetch habit entries');
      return response.json();
    },
  });

  // Fetch sequence steps for sequencing habits
  const { data: sequenceSteps = [] } = useQuery<HabitSequenceStep[]>({
    queryKey: ["/api/habits", habit.id, "sequences"],
    queryFn: async () => {
      const response = await fetch(`/api/habits/${habit.id}/sequences`);
      if (!response.ok) throw new Error('Failed to fetch sequence steps');
      return response.json();
    },
    enabled: habit.isHabitSequencing,
  });

  // Fetch linked goals for this habit
  const { data: linkedGoals = [] } = useQuery<Goal[]>({
    queryKey: ["/api/habits", habit.id, "goals"],
    queryFn: async () => {
      const response = await fetch(`/api/habits/${habit.id}/goals`);
      if (!response.ok) throw new Error('Failed to fetch habit goals');
      return response.json();
    },
  });

  const toggleHabitMutation = useMutation({
    mutationFn: async ({ date, completed }: { date: Date; completed: boolean }) => {
      return apiRequest("POST", `/api/habits/${habit.id}/entries`, {
        date: formatDateInput(date),
        completed,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/habits/${habit.id}/entries`] });
      queryClient.invalidateQueries({ queryKey: ["/api/habits"] });
      queryClient.invalidateQueries({ queryKey: ["/api/goals"] });
      queryClient.invalidateQueries({ queryKey: ["/api/stats/dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["/api/stats/habits"] });
      queryClient.invalidateQueries({ queryKey: ["/api/activities"] });
    },
    onError: () => {
    },
  });

  const deleteHabitMutation = useMutation({
    mutationFn: async () => {
      return apiRequest("DELETE", `/api/habits/${habit.id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/habits"] });
      queryClient.invalidateQueries({ queryKey: ["/api/goals"] });
      queryClient.invalidateQueries({ queryKey: ["/api/stats/dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["/api/stats/habits"] });
    },
    onError: () => {
    },
  });

  const advanceHabitStackMutation = useMutation({
    mutationFn: async ({ phase, currentTarget }: { phase: number; currentTarget: number }) => {
      return apiRequest("PATCH", `/api/habits/${habit.id}`, {
        habitStackPhase: phase,
        habitStackCurrentTarget: currentTarget,
        target: currentTarget,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/habits"] });
      queryClient.invalidateQueries({ queryKey: [`/api/habits/${habit.id}/entries`] });
      queryClient.invalidateQueries({ queryKey: ["/api/stats/habits"] });
    },
  });

  const archiveHabitMutation = useMutation({
    mutationFn: async () => {
      return apiRequest("PATCH", `/api/habits/${habit.id}`, { isActive: false });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/habits"] });
      queryClient.invalidateQueries({ queryKey: ["/api/stats/habits"] });
      queryClient.invalidateQueries({ queryKey: ["/api/stats/dashboard"] });
    },
  });

  const handleDeleteHabit = () => {
    if (confirm("Are you sure you want to delete this habit? This will also delete all associated entries.")) {
      deleteHabitMutation.mutate();
    }
  };

  const fireConfetti = useCallback(() => {
    const duration = 3000;
    const end = Date.now() + duration;
    const colors = ["#FFD700", "#FF6347", "#00CED1", "#9370DB", "#32CD32"];

    const frame = () => {
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0.7 },
        colors,
      });
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0.7 },
        colors,
      });
      if (Date.now() < end) requestAnimationFrame(frame);
    };
    frame();
  }, []);

  const entryToKey = useCallback((entryDate: string | Date): string | null => {
    // Server stores entries as UTC midnight of the local check-in date, so extracting
    // the UTC date portion (via the shared utcEntryDay helper) gives the original
    // intended local date without timezone shift.
    const d = new Date(entryDate);
    if (Number.isNaN(d.getTime())) return null;
    const utcDateStr = utcEntryDay(entryDate);

    if (habit.frequency === 'daily') {
      return utcDateStr;
    } else if (habit.frequency === 'weekly') {
      // Parse as a local date first so getDay() / setDate() work in the user's timezone.
      const localD = parseLocalDate(utcDateStr);
      const day = localD.getDay();
      const diff = localD.getDate() - day + (day === 0 ? -6 : 1);
      const weekStart = new Date(localD);
      weekStart.setDate(diff);
      return formatDateInput(weekStart);
    } else if (habit.frequency === 'monthly') {
      const [year, month] = utcDateStr.split('-');
      return `${year}-${parseInt(month)}`;
    } else if (habit.frequency === 'quarterly') {
      const [year, month] = utcDateStr.split('-');
      const quarter = Math.floor((parseInt(month) - 1) / 3) + 1;
      return `${year}-Q${quarter}`;
    }
    return utcDateStr;
  }, [habit.frequency]);

  const calculateCurrentStreak = useCallback((entries: HabitEntry[], allPeriods: { key: string; isFuture: boolean }[], toggledKey: string): number => {
    const completedKeys = new Set(
      entries
        .filter(e => e.completed && e.date)
        .map(e => entryToKey(e.date!))
        .filter((k): k is string => k !== null)
    );
    completedKeys.add(toggledKey);

    const pastPeriods = allPeriods.filter(p => !p.isFuture);
    const toggledIndex = pastPeriods.findIndex(p => p.key === toggledKey);
    if (toggledIndex < 0) return 1;

    let streak = 0;
    for (let i = toggledIndex; i >= 0; i--) {
      if (completedKeys.has(pastPeriods[i].key)) {
        streak++;
      } else {
        break;
      }
    }
    return streak;
  }, [entryToKey]);

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      "Health": "bg-red-500",
      "Productivity": "bg-blue-500",
      "Learning": "bg-green-500",
      "Wellness": "bg-purple-500",
      "Fitness": "bg-yellow-500",
      "Social": "bg-pink-500",
    };
    return colors[category] || "bg-gray-500";
  };

  type Period = {
    date: Date;
    label: string;
    key: string;
    isCurrent: boolean;
    isFuture: boolean;
  };

  const getPeriodsInView = (): Period[] => {
    const today = new Date();
    const periods: Period[] = [];

    // Always show enough periods to ensure scrolling is possible on all screen sizes
    // Use higher counts so content exceeds container width even on wide screens
    const minPeriods = {
      daily: 31,      // Full month of days ensures overflow
      weekly: 16,     // 4 months of weeks
      monthly: 12,    // Full year of months
      quarterly: 8,   // 2 years of quarters
      yearly: 5       // 5 years
    };

    const displayCount = Math.max(habit.target || 1, minPeriods[habit.frequency as keyof typeof minPeriods] || 14);

    if (habit.frequency === 'daily') {
      // Center today: show half before and half after (rounded)
      const beforeCount = Math.floor((displayCount - 1) / 2);
      const afterCount = displayCount - 1 - beforeCount;

      for (let i = -beforeCount; i <= afterCount; i++) {
        const date = new Date(today);
        date.setDate(today.getDate() + i);
        const isCurrent = formatDateInput(date) === formatDateInput(today);
        const isFuture = date > today;

        periods.push({
          date,
          label: date.getDate().toString(),
          key: formatDateInput(date),
          isCurrent,
          isFuture,
        });
      }
    } else if (habit.frequency === 'weekly') {
      const getWeekStartDate = (date: Date) => {
        const day = date.getDay();
        const diff = date.getDate() - day + (day === 0 ? -6 : 1);
        return new Date(date.setDate(diff));
      };

      const currentWeekStart = getWeekStartDate(new Date(today));

      // Center current week: show half before and half after (rounded)
      const beforeCount = Math.floor((displayCount - 1) / 2);
      const afterCount = displayCount - 1 - beforeCount;

      for (let i = -beforeCount; i <= afterCount; i++) {
        const date = new Date(today);
        date.setDate(today.getDate() + (i * 7));
        const weekStart = getWeekStartDate(new Date(date));
        const weekNum = getWeek(weekStart, { weekStartsOn: 1 });
        const isCurrent = formatDateInput(weekStart) === formatDateInput(currentWeekStart);
        const isFuture = weekStart > currentWeekStart;

        periods.push({
          date: weekStart,
          label: `W${weekNum}`,
          key: formatDateInput(weekStart),
          isCurrent,
          isFuture,
        });
      }
    } else if (habit.frequency === 'monthly') {
      // Center current month: show half before and half after (rounded)
      const beforeCount = Math.floor((displayCount - 1) / 2);
      const afterCount = displayCount - 1 - beforeCount;

      for (let i = -beforeCount; i <= afterCount; i++) {
        const date = new Date(today.getFullYear(), today.getMonth() + i, 1);
        const currentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const isCurrent = date.getTime() === currentMonth.getTime();
        const isFuture = date > currentMonth;

        periods.push({
          date,
          label: date.toLocaleDateString('en-US', { month: 'short' }),
          key: `${date.getFullYear()}-${date.getMonth() + 1}`,
          isCurrent,
          isFuture,
        });
      }
    } else if (habit.frequency === 'quarterly') {
      const getQuarter = (date: Date) => Math.floor(date.getMonth() / 3) + 1;
      const getQuarterStart = (year: number, quarter: number) => {
        return new Date(year, (quarter - 1) * 3, 1);
      };

      const currentQuarter = getQuarter(today);
      const currentYear = today.getFullYear();
      const currentQuarterStart = getQuarterStart(currentYear, currentQuarter);

      // Center current quarter: show half before and half after (rounded)
      const beforeCount = Math.floor((displayCount - 1) / 2);
      const afterCount = displayCount - 1 - beforeCount;

      for (let i = -beforeCount; i <= afterCount; i++) {
        let quarter = currentQuarter + i;
        let year = currentYear;

        while (quarter <= 0) {
          quarter += 4;
          year -= 1;
        }
        while (quarter > 4) {
          quarter -= 4;
          year += 1;
        }

        const date = getQuarterStart(year, quarter);
        const isCurrent = date.getTime() === currentQuarterStart.getTime();
        const isFuture = date > currentQuarterStart;

        periods.push({
          date,
          label: `Q${quarter}`,
          key: `${year}-Q${quarter}`,
          isCurrent,
          isFuture,
        });
      }
    }

    return periods;
  };

  const isHabitCompleted = (period: Period) => {
    // For daily habits, match exact date
    if (habit.frequency === 'daily') {
      return habitEntries.some(entry => {
        if (!entry.date || !entry.completed) return false;
        return utcEntryDay(entry.date) === period.key;
      });
    }

    // For other frequencies, use entryToKey which handles weekly/monthly/quarterly keys
    // correctly (extracting UTC date then computing period boundary in local timezone).
    return habitEntries.some(entry => {
      if (!entry.date || !entry.completed) return false;
      const key = entryToKey(entry.date);
      return key !== null && key === period.key;
    });
  };

  const handleTogglePeriod = async (period: Period) => {
    const completed = isHabitCompleted(period);
    const newCompleted = !completed;

    // Intercept for habit sequencing — only when marking complete with steps defined
    if (habit.isHabitSequencing && newCompleted && sequenceSteps.length > 0) {
      setSequencingPeriod({ date: period.date, key: period.key, completed: newCompleted });
      setCurrentStepIndex(0);
      setCompletedStepIds(new Set());
      setSequencingDialogOpen(true);
      return;
    }

    const prevCount = getCompletedCount();
    const currentTarget = habit.isHabitStack ? (habit.habitStackCurrentTarget || habit.target) : habit.target;
    const shouldShowTargetReached = !habit.isHabitStack && newCompleted && habit.target > 0 && prevCount < habit.target && prevCount + 1 >= habit.target;

    const shouldShowHabitStack = habit.isHabitStack && newCompleted && currentTarget > 0;
    let habitStackTriggered = false;

    if (shouldShowHabitStack) {
      const streak = calculateCurrentStreak(habitEntries, periods, period.key);
      if (streak >= currentTarget) {
        habitStackTriggered = true;
        const phase = habit.habitStackPhase || 1;
        if (celebrationEnabled && streak >= 3) {
          pendingHabitStackRef.current = phase;
          setTimeout(() => {
            fireConfetti();
            setStreakCelebration({ streak, remaining: 0 });
          }, 300);
        } else {
          setTimeout(() => {
            fireConfetti();
            setHabitStackPhaseComplete(phase);
          }, 300);
        }
      }
    }

    if (!habitStackTriggered) {
      if (newCompleted && celebrationEnabled) {
        const streak = calculateCurrentStreak(habitEntries, periods, period.key);
        if (streak >= 3) {
          const remaining = Math.max(0, habit.target - streak);
          if (shouldShowTargetReached) {
            pendingTargetReachedRef.current = true;
          }
          setTimeout(() => {
            fireConfetti();
            setStreakCelebration({ streak, remaining });
          }, 300);
        } else if (shouldShowTargetReached) {
          setTimeout(() => {
            setTargetReached(true);
          }, 300);
        }
      } else if (shouldShowTargetReached) {
        setTimeout(() => {
          setTargetReached(true);
        }, 300);
      }
    }

    toggleMutationPromiseRef.current = toggleHabitMutation.mutateAsync({ date: period.date, completed: newCompleted }).catch(() => {});
  };

  const getCompletedCount = () => {
    return habitEntries.filter(entry => entry.completed).length;
  };

  const getProgressPercentage = () => {
    if (habit.target === 0) return 0;
    return Math.min(Math.round((getCompletedCount() / habit.target) * 100), 100);
  };

  const periods = getPeriodsInView();

  const isMissedSequenceDay = useCallback((period: Period): boolean => {
    if (!habit.sequenceSuccessing) return false;
    if (period.isFuture) return false;
    return !isHabitCompleted(period);
  }, [habit.sequenceSuccessing, habitEntries]);

  const getSequenceCount = useCallback((): number => {
    if (!habit.sequenceSuccessing) return getCompletedCount();
    const completedKeys = new Set(
      habitEntries
        .filter(e => e.completed && e.date)
        .map(e => entryToKey(e.date!))
        .filter((k): k is string => k !== null)
    );
    const nonFuturePeriods = periods.filter(p => !p.isFuture);
    let count = 0;
    for (let i = nonFuturePeriods.length - 1; i >= 0; i--) {
      if (completedKeys.has(nonFuturePeriods[i].key)) {
        count++;
      } else {
        break;
      }
    }
    return count;
  }, [habit.sequenceSuccessing, periods, habitEntries, entryToKey]);

  // Scroll to center the current period in the checkbox row (within the container only)
  useEffect(() => {
    if (viewMode === "list" && scrollContainerRef.current && periods.length > 0 && !hasScrolledRef.current) {
      // Delay to ensure DOM is fully rendered
      const timer = setTimeout(() => {
        const container = scrollContainerRef.current;
        if (!container || hasScrolledRef.current) return;

        // Find the current period wrapper div using data attribute
        const currentPeriodElement = container.querySelector('[data-current="true"]') as HTMLElement;

        if (currentPeriodElement) {
          const containerRect = container.getBoundingClientRect();
          const periodRect = currentPeriodElement.getBoundingClientRect();

          // Calculate the offset needed to center the period in the container
          const currentPeriodCenter = periodRect.left + periodRect.width / 2;
          const containerCenter = containerRect.left + containerRect.width / 2;
          const scrollOffset = currentPeriodCenter - containerCenter;
          const newScrollLeft = container.scrollLeft + scrollOffset;

          container.scrollTo({
            left: Math.max(0, newScrollLeft),
            behavior: 'smooth'
          });
          hasScrolledRef.current = true;
        }
      }, 150);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [viewMode, habit.id, habitEntries, periods.length]);

  // Reset scroll flag when view mode changes
  useEffect(() => {
    hasScrolledRef.current = false;
  }, [viewMode]);

  const handleDismissCelebration = useCallback(() => {
    setStreakCelebration(null);
    celebrationJustDismissedRef.current = true;
    if (pendingHabitStackRef.current !== null) {
      const phase = pendingHabitStackRef.current;
      pendingHabitStackRef.current = null;
      setTimeout(() => {
        setHabitStackPhaseComplete(phase);
      }, 200);
    } else if (pendingTargetReachedRef.current) {
      pendingTargetReachedRef.current = false;
      setTimeout(() => {
        setTargetReached(true);
      }, 200);
    }
    setTimeout(() => {
      celebrationJustDismissedRef.current = false;
    }, 300);
  }, []);

  const handleDismissTargetReached = useCallback(() => {
    setTargetReached(false);
    celebrationJustDismissedRef.current = true;
    setTimeout(() => {
      celebrationJustDismissedRef.current = false;
    }, 300);
  }, []);

  const handleCardClick = useCallback(() => {
    if (celebrationJustDismissedRef.current || streakCelebration || targetReached || habitStackPhaseComplete !== null) return;
    setEditModalOpen(true);
  }, [streakCelebration, targetReached, habitStackPhaseComplete]);

  const streakCelebrationDialog = (
    <Dialog open={!!streakCelebration} onOpenChange={(open) => !open && handleDismissCelebration()}>
      <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto text-center" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle className="flex items-center justify-center gap-2 text-2xl">
            <Trophy className="w-8 h-8 text-yellow-500" />
            Great Job!
            <Trophy className="w-8 h-8 text-yellow-500" />
          </DialogTitle>
        </DialogHeader>
        <div className="py-6 space-y-4">
          <div className="text-6xl">🔥</div>
          <p className="text-xl font-semibold text-foreground">
            You have completed{" "}
            <span className="text-primary text-2xl">{streakCelebration?.streak}</span>{" "}
            in a row!
          </p>
          {streakCelebration && streakCelebration.remaining > 0 ? (
            <p className="text-muted-foreground">
              Only <span className="font-bold text-foreground">{streakCelebration.remaining}</span> more to reach your target of {habit.target}!
            </p>
          ) : (
            <p className="text-green-600 dark:text-green-400 font-semibold">
              You've reached your target! Keep the streak going!
            </p>
          )}
        </div>
        <Button onClick={handleDismissCelebration} className="w-full">
          Keep Going!
        </Button>
      </DialogContent>
    </Dialog>
  );

  const targetReachedDialog = (
    <Dialog open={targetReached} onOpenChange={(open) => !open && handleDismissTargetReached()}>
      <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto text-center" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle className="flex items-center justify-center gap-2 text-2xl">
            <Medal className="w-8 h-8 text-yellow-500" />
            Target Reached! 🎯
            <Medal className="w-8 h-8 text-yellow-500" />
          </DialogTitle>
        </DialogHeader>
        <div className="py-6 space-y-4">
          <div className="text-6xl">🏆</div>
          <p className="text-xl font-semibold text-foreground">
            You've completed <span className="text-primary">{habit.name}</span>{" "}
            <span className="text-primary text-2xl">{habit.target}</span> times!
          </p>
          <p className="text-muted-foreground">
            What would you like to do?
          </p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={handleDismissTargetReached} className="flex-1">
            Keep Going
          </Button>
          <Button
            onClick={() => {
              archiveHabitMutation.mutate();
              handleDismissTargetReached();
            }}
            className="flex-1"
          >
            <Archive className="w-4 h-4 mr-2" />
            Archive Habit
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  const handleDismissHabitStack = useCallback(() => {
    setHabitStackPhaseComplete(null);
    setHabitStackSelectedDays("");
    setHabitStackCustomDays("");
    celebrationJustDismissedRef.current = true;
    setTimeout(() => {
      celebrationJustDismissedRef.current = false;
    }, 300);
  }, []);

  const handleHabitStackAdvance = useCallback(async (nextPhase: number, newTarget: number) => {
    if (toggleMutationPromiseRef.current) {
      await toggleMutationPromiseRef.current;
      toggleMutationPromiseRef.current = null;
    }
    advanceHabitStackMutation.mutate({ phase: nextPhase, currentTarget: newTarget });
    handleDismissHabitStack();
  }, [advanceHabitStackMutation, handleDismissHabitStack]);

  const habitStackDialog = (
    <Dialog open={habitStackPhaseComplete !== null} onOpenChange={(open) => !open && handleDismissHabitStack()}>
      <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto text-center" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle className="flex items-center justify-center gap-2 text-2xl">
            <Layers className="w-8 h-8 text-blue-500" />
            Phase Complete!
            <Layers className="w-8 h-8 text-blue-500" />
          </DialogTitle>
        </DialogHeader>
        {habitStackPhaseComplete === 1 && (
          <div className="py-4 space-y-4">
            <div className="text-5xl">🎉</div>
            <p className="text-lg font-semibold text-foreground">
              Amazing! You completed{" "}
              <span className="text-primary">{habit.habitStackCurrentTarget || habit.habitStackStartDays}</span>{" "}
              consecutive days of <span className="text-primary">{habit.name}</span>!
            </p>
            <p className="text-muted-foreground">
              Ready to level up? Choose your next target:
            </p>
            <ToggleGroup
              type="single"
              value={habitStackSelectedDays}
              onValueChange={setHabitStackSelectedDays}
              className="justify-center"
            >
              <ToggleGroupItem value="3" className="px-6">3 Days</ToggleGroupItem>
              <ToggleGroupItem value="4" className="px-6">4 Days</ToggleGroupItem>
              <ToggleGroupItem value="5" className="px-6">5 Days</ToggleGroupItem>
            </ToggleGroup>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={handleDismissHabitStack} className="flex-1">
                Not Now
              </Button>
              <Button
                onClick={() => {
                  if (habitStackSelectedDays) {
                    handleHabitStackAdvance(2, parseInt(habitStackSelectedDays));
                  }
                }}
                disabled={!habitStackSelectedDays}
                className="flex-1"
              >
                Continue for {habitStackSelectedDays || "?"} Days
              </Button>
            </div>
          </div>
        )}
        {habitStackPhaseComplete === 2 && (
          <div className="py-4 space-y-4">
            <div className="text-5xl">🌟</div>
            <p className="text-lg font-semibold text-foreground">
              Incredible! You completed{" "}
              <span className="text-primary">{habit.habitStackCurrentTarget}</span>{" "}
              consecutive days of <span className="text-primary">{habit.name}</span>!
            </p>
            <p className="text-muted-foreground">
              How many more days do you want to continue this habit?
            </p>
            <div className="flex items-center justify-center gap-2">
              <Input
                type="number"
                min={1}
                max={365}
                placeholder="Enter number of days"
                value={habitStackCustomDays}
                onChange={(e) => setHabitStackCustomDays(e.target.value)}
                className="max-w-[200px] text-center"
              />
              <span className="text-muted-foreground">days</span>
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={handleDismissHabitStack} className="flex-1">
                Not Now
              </Button>
              <Button
                onClick={() => {
                  const days = parseInt(habitStackCustomDays);
                  if (days > 0) {
                    handleHabitStackAdvance(3, days);
                  }
                }}
                disabled={!habitStackCustomDays || parseInt(habitStackCustomDays) <= 0}
                className="flex-1"
              >
                Continue for {habitStackCustomDays || "?"} Days
              </Button>
            </div>
          </div>
        )}
        {habitStackPhaseComplete !== null && habitStackPhaseComplete >= 3 && (
          <div className="py-4 space-y-4">
            <div className="text-5xl">🏆</div>
            <p className="text-lg font-semibold text-foreground">
              You've completed{" "}
              <span className="text-primary">{habit.habitStackCurrentTarget}</span>{" "}
              consecutive days of <span className="text-primary">{habit.name}</span>!
            </p>
            <p className="text-muted-foreground">
              How many more days do you want to continue?
            </p>
            <div className="flex items-center justify-center gap-2">
              <Input
                type="number"
                min={1}
                max={365}
                placeholder="Enter number of days"
                value={habitStackCustomDays}
                onChange={(e) => setHabitStackCustomDays(e.target.value)}
                className="max-w-[200px] text-center"
              />
              <span className="text-muted-foreground">days</span>
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => {
                archiveHabitMutation.mutate();
                handleDismissHabitStack();
              }} className="flex-1">
                <Archive className="w-4 h-4 mr-2" />
                Archive
              </Button>
              <Button
                onClick={() => {
                  const days = parseInt(habitStackCustomDays);
                  if (days > 0) {
                    handleHabitStackAdvance(habitStackPhaseComplete! + 1, days);
                  }
                }}
                disabled={!habitStackCustomDays || parseInt(habitStackCustomDays) <= 0}
                className="flex-1"
              >
                Continue for {habitStackCustomDays || "?"} Days
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );

  const handleSequencingComplete = () => {
    if (!sequencingPeriod) return;
    setSequencingDialogOpen(false);
    const period = sequencingPeriod;
    setSequencingPeriod(null);

    const prevCount = getCompletedCount();
    const currentTarget = habit.isHabitStack ? (habit.habitStackCurrentTarget || habit.target) : habit.target;
    const newCompleted = period.completed;
    const shouldShowTargetReached = !habit.isHabitStack && newCompleted && habit.target > 0 && prevCount < habit.target && prevCount + 1 >= habit.target;

    if (newCompleted && celebrationEnabled) {
      const allPeriods = getPeriodsInView();
      const streak = calculateCurrentStreak(habitEntries, allPeriods, period.key);
      if (streak >= 3) {
        const remaining = Math.max(0, habit.target - streak);
        if (shouldShowTargetReached) pendingTargetReachedRef.current = true;
        setTimeout(() => { fireConfetti(); setStreakCelebration({ streak, remaining }); }, 300);
      } else if (shouldShowTargetReached) {
        setTimeout(() => setTargetReached(true), 300);
      }
    } else if (shouldShowTargetReached) {
      setTimeout(() => setTargetReached(true), 300);
    }

    toggleMutationPromiseRef.current = toggleHabitMutation.mutateAsync({ date: period.date, completed: newCompleted }).catch(() => {});
  };

  const currentSeqStep = sequenceSteps[currentStepIndex];

  const sequencingDialog = (
    <Dialog open={sequencingDialogOpen} onOpenChange={(open) => { if (!open) { setSequencingDialogOpen(false); setSequencingPeriod(null); } }}>
      <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <ListOrdered className="w-6 h-6 text-primary" />
            Habit Sequencing
          </DialogTitle>
        </DialogHeader>
        <div className="py-4 space-y-6">
          {/* Progress bar */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                Step {Math.min(currentStepIndex + 1, sequenceSteps.length)} of {sequenceSteps.length}
              </span>
              <span className="font-medium text-primary">
                {completedStepIds.size} completed
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div
                className="bg-primary rounded-full h-2 transition-all duration-300"
                style={{ width: `${sequenceSteps.length > 0 ? (completedStepIds.size / sequenceSteps.length) * 100 : 0}%` }}
              />
            </div>
          </div>

          {/* All steps overview */}
          <div className="space-y-2">
            {sequenceSteps.map((step, idx) => {
              const isCompleted = completedStepIds.has(step.id);
              const isCurrent = idx === currentStepIndex;
              return (
                <div
                  key={step.id}
                  className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                    isCompleted
                      ? "bg-primary/5 border-primary/20 opacity-60"
                      : isCurrent
                      ? "bg-primary/10 border-primary"
                      : "bg-background border-border opacity-40"
                  }`}
                >
                  <div className="flex-shrink-0 mt-0.5">
                    {isCompleted ? (
                      <CheckCircle2 className="w-5 h-5 text-primary" />
                    ) : (
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center text-xs font-bold ${isCurrent ? "border-primary text-primary" : "border-muted-foreground text-muted-foreground"}`}>
                        {idx + 1}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${isCompleted ? "line-through text-muted-foreground" : isCurrent ? "text-foreground" : "text-muted-foreground"}`}>
                      {step.description}
                    </p>
                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                      <span className="text-xs text-muted-foreground capitalize">{step.frequency}</span>
                      {step.timeOfDay && (
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Clock className="w-3 h-3" />
                          {step.timeOfDay}
                        </span>
                      )}
                      {step.place && (
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <MapPin className="w-3 h-3" />
                          {step.place}
                        </span>
                      )}
                      {((step.daysOfWeek as string[]) || []).length > 0 && (
                        <span className="text-xs text-muted-foreground">
                          {(step.daysOfWeek as string[]).join(", ")}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Current step action */}
          {currentSeqStep && !completedStepIds.has(currentSeqStep.id) ? (
            <div className="space-y-3">
              <div className="rounded-lg bg-primary/10 border border-primary p-4 space-y-1">
                <p className="text-xs font-semibold text-primary uppercase tracking-wider">Current Step</p>
                <p className="text-base font-semibold text-foreground">{currentSeqStep.description}</p>
                <div className="flex flex-wrap gap-3 mt-1">
                  {currentSeqStep.timeOfDay && (
                    <span className="text-sm text-muted-foreground flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" /> {currentSeqStep.timeOfDay}
                    </span>
                  )}
                  {currentSeqStep.place && (
                    <span className="text-sm text-muted-foreground flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5" /> {currentSeqStep.place}
                    </span>
                  )}
                </div>
              </div>
              <Button
                className="w-full"
                onClick={() => {
                  const newCompleted = new Set(completedStepIds);
                  newCompleted.add(currentSeqStep.id);
                  setCompletedStepIds(newCompleted);
                  if (currentStepIndex + 1 < sequenceSteps.length) {
                    setCurrentStepIndex(currentStepIndex + 1);
                  }
                }}
                data-testid="button-complete-sequence-step"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Complete Step
              </Button>
            </div>
          ) : completedStepIds.size === sequenceSteps.length ? (
            <div className="space-y-3">
              <div className="text-center py-2">
                <div className="text-4xl mb-2">🎉</div>
                <p className="text-lg font-semibold text-foreground">All steps complete!</p>
                <p className="text-sm text-muted-foreground">Mark your habit as done for today.</p>
              </div>
              <Button className="w-full" onClick={handleSequencingComplete} data-testid="button-finish-sequencing">
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Mark Habit Complete
              </Button>
            </div>
          ) : null}
        </div>
        <Button
          variant="outline"
          className="w-full"
          onClick={() => { setSequencingDialogOpen(false); setSequencingPeriod(null); }}
        >
          Cancel
        </Button>
      </DialogContent>
    </Dialog>
  );

  const cardDialogs = (
    <>
      <EditHabitModal
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
        habit={habit}
      />
      <CreateNoteModal
        isOpen={createNoteModalOpen}
        onClose={() => setCreateNoteModalOpen(false)}
        entityType="habit"
        entityId={habit.id}
        entityTitle={habit.name}
      />
      <ShareDialog
        isOpen={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        itemId={habit.id}
        itemType="habit"
        itemTitle={habit.name}
      />
      {streakCelebrationDialog}
      {targetReachedDialog}
      {habitStackDialog}
      {sequencingDialog}
    </>
  );

  if (viewMode === "list") {
    return (
      <Card
        className="w-full cursor-pointer hover:shadow-md transition-shadow"
        data-testid={`card-habit-${habit.id}`}
        onClick={handleCardClick}
      >
        <CardContent className="p-3 sm:p-4">
          {/* Mobile: Stacked layout, Desktop: Grid layout with constrained columns */}
          <div className="flex flex-col gap-3 sm:grid sm:grid-cols-[auto_minmax(0,1fr)_auto] sm:items-center sm:gap-4">
            {/* Habit Info - Top on mobile, Left on desktop */}
            <div className="flex items-start justify-between sm:w-64">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <div className={`w-3 h-3 flex-shrink-0 ${getCategoryColor(habit.category)} rounded-full`}></div>
                  <h3 className="text-sm sm:text-base font-semibold text-foreground truncate" data-testid={`text-title-${habit.id}`}>
                    {habit.name}
                  </h3>
                </div>
                <div className="text-xs sm:text-sm text-muted-foreground flex items-center gap-1">
                  Target: {habit.target} ({habit.frequency})
                  {habit.isHabitStack && (
                    <Badge variant="secondary" className="text-xs px-1.5 py-0">
                      <Layers className="w-3 h-3 mr-0.5" />
                      Stack
                    </Badge>
                  )}
                  {habit.sequenceSuccessing && (
                    <Badge variant="secondary" className="text-xs px-1.5 py-0 border-red-300 dark:border-red-700">
                      <Link className="w-3 h-3 mr-0.5" />
                      Sequence
                    </Badge>
                  )}
                </div>
                {linkedGoals.length > 0 && (
                  <div className="flex items-center gap-1 mt-1 sm:mt-2 flex-wrap" onClick={(e) => e.stopPropagation()}>
                    <Target className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                    {linkedGoals.map(goal => (
                      <Badge key={goal.id} variant="outline" className="text-xs" data-testid={`badge-linked-goal-${goal.id}`}>
                        {goal.title}
                      </Badge>
                    ))}
                  </div>
                )}
                <HabitTrackerXChips habitId={habit.id} />
              </div>

              {/* Mobile: Menu in header row */}
              <div className="flex items-center gap-2 sm:hidden" onClick={(e) => e.stopPropagation()}>
                {(() => {
                  const streak = habit.currentStreak ?? 0;
                  const checkedInToday = habit.checkedInToday ?? false;
                  const dueToday = habit.dueToday ?? false;
                  const badgeState =
                    streak > 0 && checkedInToday ? "done" :
                    streak > 0 && dueToday ? "pending" :
                    "broken";
                  const flameColor =
                    badgeState === "pending" ? "text-amber-500" : "text-orange-500";
                  return streak > 0 ? (
                    <div className={`flex items-center gap-1 ${flameColor}`}>
                      <Flame className="w-4 h-4" />
                      <span className="text-xs font-medium">{streak}{badgeState === "pending" ? " ●" : ""}</span>
                    </div>
                  ) : null;
                })()}
                <Badge variant={getProgressPercentage() >= 100 ? "default" : "secondary"} className="text-xs">
                  {getProgressPercentage()}%
                </Badge>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      data-testid={`button-menu-mobile-${habit.id}`}
                    >
                      <MoreHorizontal className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onSelect={() => setCreateNoteModalOpen(true)}
                      data-testid={`button-create-note-habit-mobile-${habit.id}`}
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Create Note
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={() => setShareDialogOpen(true)}
                      data-testid={`button-share-mobile-${habit.id}`}
                    >
                      <Share2 className="w-4 h-4 mr-2" />
                      Share
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={() => setEditModalOpen(true)}
                      data-testid={`button-edit-mobile-${habit.id}`}
                    >
                      <Pencil className="w-4 h-4 mr-2" />
                      Edit Habit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={handleDeleteHabit}
                      className="text-destructive focus:text-destructive"
                      data-testid={`button-delete-mobile-${habit.id}`}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Habit
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Period Tracking - Below on mobile, Right side on desktop */}
            <div className="w-full overflow-hidden">
              <div
                ref={scrollContainerRef}
                className="overflow-x-auto overflow-y-hidden scrollbar-thin"
                style={{ WebkitOverflowScrolling: 'touch' }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="inline-flex gap-0.5 sm:gap-1 px-1">
                {periods.map((period, index) => {
                  const isChecked = isHabitCompleted(period);
                  const isMissed = isMissedSequenceDay(period);

                  return (
                    <div
                      key={period.key}
                      className={`flex flex-col items-center flex-shrink-0 rounded px-1 sm:px-0.5 py-1 sm:py-0.5 ${isMissed ? 'bg-red-100 dark:bg-red-950' : ''}`}
                      data-current={period.isCurrent ? "true" : undefined}
                    >
                      <div className={`text-[10px] sm:text-xs mb-1 whitespace-nowrap ${isMissed ? 'text-red-600 dark:text-red-400 font-semibold' : period.isFuture ? 'text-muted-foreground/40' : 'text-muted-foreground'} ${period.isCurrent ? 'font-bold text-primary' : ''}`}>
                        {period.label}
                      </div>
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!period.isFuture) handleTogglePeriod(period);
                        }}
                        onKeyDown={(e) => {
                          if ((e.key === 'Enter' || e.key === ' ') && !period.isFuture) {
                            e.preventDefault();
                            e.stopPropagation();
                            handleTogglePeriod(period);
                          }
                        }}
                        className={`min-w-[28px] min-h-[28px] sm:min-w-0 sm:min-h-0 flex items-center justify-center ${period.isFuture ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}`}
                        data-testid={`checkbox-wrapper-${habit.id}-period-${index}`}
                      >
                        <Checkbox
                          id={`${habit.id}-${period.key}`}
                          checked={isChecked}
                          onCheckedChange={() => !period.isFuture && handleTogglePeriod(period)}
                          onClick={(e) => e.stopPropagation()}
                          disabled={period.isFuture}
                          className={`w-5 h-5 pointer-events-none ${period.isCurrent ? 'ring-2 ring-primary ring-offset-1' : ''} ${period.isFuture ? 'opacity-30 cursor-not-allowed' : ''} ${isMissed ? 'border-red-400 dark:border-red-600' : ''}`}
                          data-testid={`checkbox-habit-${habit.id}-period-${index}`}
                          tabIndex={-1}
                        />
                      </div>
                    </div>
                  );
                })}
                </div>
              </div>
            </div>

            {/* Desktop only: Progress Badge and Menu */}
            <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
              {(() => {
                const streak = habit.currentStreak ?? 0;
                const checkedInToday = habit.checkedInToday ?? false;
                const dueToday = habit.dueToday ?? false;
                const badgeState =
                  streak > 0 && checkedInToday ? "done" :
                  streak > 0 && dueToday ? "pending" :
                  "broken";
                const flameColor =
                  badgeState === "pending" ? "text-amber-500" : "text-orange-500";
                return streak > 0 ? (
                  <div className={`flex items-center gap-1 ${flameColor}`}>
                    <Flame className="w-4 h-4" />
                    <span className="text-xs font-medium">{streak}{badgeState === "pending" ? " ●" : ""}</span>
                  </div>
                ) : null;
              })()}
              <Badge variant={getProgressPercentage() >= 100 ? "default" : "secondary"} className="text-xs">
                {getProgressPercentage()}%
              </Badge>
              <div onClick={(e) => e.stopPropagation()}>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      data-testid={`button-menu-${habit.id}`}
                    >
                      <MoreHorizontal className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onSelect={() => setCreateNoteModalOpen(true)}
                      data-testid={`button-create-note-habit-${habit.id}`}
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Create Note
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={() => setShareDialogOpen(true)}
                      data-testid={`button-share-${habit.id}`}
                    >
                      <Share2 className="w-4 h-4 mr-2" />
                      Share
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={() => setEditModalOpen(true)}
                      data-testid={`button-edit-${habit.id}`}
                    >
                      <Pencil className="w-4 h-4 mr-2" />
                      Edit Habit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={handleDeleteHabit}
                      className="text-destructive focus:text-destructive"
                      data-testid={`button-delete-${habit.id}`}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Habit
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>
        </CardContent>
        {cardDialogs}
      </Card>
    );
  }

  // Grid View
  return (
    <Card
      className="habit-card cursor-pointer hover:shadow-lg transition-shadow"
      data-testid={`card-habit-${habit.id}`}
      onClick={handleCardClick}
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className={`w-3 h-3 ${getCategoryColor(habit.category)} rounded-full`}></div>
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              {habit.category}
            </span>
          </div>
          <div onClick={(e) => e.stopPropagation()}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  data-testid={`button-menu-${habit.id}`}
                >
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onSelect={() => setCreateNoteModalOpen(true)}
                data-testid={`button-create-note-habit-${habit.id}`}
              >
                <FileText className="w-4 h-4 mr-2" />
                Create Note
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => setShareDialogOpen(true)}
                data-testid={`button-share-${habit.id}`}
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => setEditModalOpen(true)}
                data-testid={`button-edit-${habit.id}`}
              >
                <Pencil className="w-4 h-4 mr-2" />
                Edit Habit
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={handleDeleteHabit}
                className="text-destructive focus:text-destructive"
                data-testid={`button-delete-${habit.id}`}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Habit
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </div>

        <h3 className="text-lg font-semibold text-foreground mb-2" data-testid={`text-title-${habit.id}`}>
          {habit.name}
        </h3>
        {habit.description && (
          <p className="text-muted-foreground text-sm mb-4 line-clamp-2">{habit.description}</p>
        )}

        {/* Linked Goals */}
        {linkedGoals.length > 0 && (
          <div className="mb-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Target className="w-3 h-3" />
                <span>Goals:</span>
              </div>
              {linkedGoals.map(goal => (
                <Badge key={goal.id} variant="outline" className="text-xs" data-testid={`badge-linked-goal-${goal.id}`}>
                  {goal.title}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* TrackerX quantified progress (grid view) */}
        <div className="mb-2">
          <HabitTrackerXChips habitId={habit.id} />
        </div>

        {/* Progress Summary */}
        <div className="mb-4" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-foreground">Progress</span>
            <div className="flex items-center space-x-2">
              {(() => {
                const streak = habit.currentStreak ?? 0;
                const checkedInToday = habit.checkedInToday ?? false;
                const dueToday = habit.dueToday ?? false;
                const badgeState =
                  streak > 0 && checkedInToday ? "done" :
                  streak > 0 && dueToday ? "pending" :
                  "broken";
                const flameColor =
                  badgeState === "pending" ? "text-amber-500" : "text-orange-500";
                return streak > 0 ? (
                  <div className={`flex items-center gap-1 ${flameColor}`}>
                    <Flame className="w-4 h-4" />
                    <span className="text-xs font-medium">{streak}{badgeState === "pending" ? " ●" : ""}</span>
                  </div>
                ) : (
                  <Flame className="w-4 h-4 text-muted-foreground/40" />
                );
              })()}
              <span className="text-sm font-medium text-foreground flex items-center gap-1">
                Target: {habit.target}
                {habit.isHabitStack && (
                  <Badge variant="secondary" className="text-xs px-1.5 py-0">
                    <Layers className="w-3 h-3 mr-0.5" />
                    Stack
                  </Badge>
                )}
                {habit.sequenceSuccessing && (
                  <Badge variant="secondary" className="text-xs px-1.5 py-0 border-red-300 dark:border-red-700">
                    <Link className="w-3 h-3 mr-0.5" />
                    Sequence
                  </Badge>
                )}
              </span>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-muted rounded-full h-2 mb-2">
            <div
              className={`rounded-full h-2 transition-all duration-300 ${habit.sequenceSuccessing ? 'bg-orange-500' : 'bg-primary'}`}
              style={{ width: `${habit.sequenceSuccessing ? Math.min(Math.round((getSequenceCount() / Math.max(habit.target, 1)) * 100), 100) : getProgressPercentage()}%` }}
            />
          </div>

          <div className="text-xs text-muted-foreground text-center">
            {habit.sequenceSuccessing ? (
              <span>{getSequenceCount()} of {habit.target} consecutive {getSequenceCount() >= habit.target ? '- Sequence Complete!' : ''}</span>
            ) : (
              <span>{getProgressPercentage()}% complete</span>
            )}
          </div>
        </div>

        {/* Recent Periods Quick View */}
        <div className="mb-4" onClick={(e) => e.stopPropagation()}>
          <div className="text-sm font-medium text-foreground mb-2">
            {habit.frequency === 'daily' ? 'Last 7 Days' :
             habit.frequency === 'weekly' ? 'Last 4 Weeks' :
             habit.frequency === 'monthly' ? 'Last 6 Months' :
             'Last 4 Quarters'}
          </div>
          <div className={`grid gap-2 ${
            habit.frequency === 'daily' ? 'grid-cols-7' :
            habit.frequency === 'weekly' ? 'grid-cols-4' :
            habit.frequency === 'monthly' ? 'grid-cols-6' :
            'grid-cols-4'
          }`}>
            {periods.filter(p => !p.isFuture).slice(
              habit.frequency === 'daily' ? -7 :
              habit.frequency === 'weekly' ? -4 :
              habit.frequency === 'monthly' ? -6 :
              -4
            ).map((period, index) => {
              const isChecked = isHabitCompleted(period);
              const isMissed = isMissedSequenceDay(period);

              return (
                <div key={period.key} className={`text-center rounded p-1 ${isMissed ? 'bg-red-100 dark:bg-red-950' : ''}`}>
                  <div className={`text-xs mb-2 ${isMissed ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-muted-foreground'}`}>
                    {period.label}
                  </div>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTogglePeriod(period);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        handleTogglePeriod(period);
                      }
                    }}
                    className="min-w-[28px] min-h-[28px] sm:min-w-0 sm:min-h-0 flex items-center justify-center mx-auto cursor-pointer"
                    data-testid={`checkbox-wrapper-grid-${habit.id}-period-${index}`}
                  >
                    <Checkbox
                      id={`${habit.id}-${period.key}-grid`}
                      checked={isChecked}
                      onCheckedChange={() => handleTogglePeriod(period)}
                      onClick={(e) => e.stopPropagation()}
                      className={`pointer-events-none ${period.isCurrent ? 'ring-2 ring-primary' : ''} ${isMissed ? 'border-red-400 dark:border-red-600' : ''}`}
                      data-testid={`checkbox-habit-${habit.id}-period-grid-${index}`}
                      tabIndex={-1}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <Badge variant="secondary" className="text-xs">
            {habit.frequency}
          </Badge>
          <span>
            Created {new Date(habit.createdAt).toLocaleDateString()}
          </span>
        </div>
      </CardContent>
      {cardDialogs}
    </Card>
  );
}
